// src/hooks/useStaffTimesheet.js
//
// Pass 83C / 83C.1 — the instructor's own weekly timesheet. Scope-bound to uid+org (83B.2 pattern). It
// subscribes to the member's OWN canonical bookings + lessonInstances (org-scoped; never a second spreadsheet
// reader) and loads the persisted timesheet. §9/§10/§11 — EVERY required evidence reader must initialize
// before the timesheet is usable, and ANY reader FAILURE enters an explicit error/unverified state (a failure
// is NEVER treated as "no work"). It NEVER creates lessonInstances or writes source data.

import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeBooking } from '../utils/booking';
import { normalizeLessonInstanceRecord } from '../utils/lessonInstancePersistence';
import { normalizeTimesheetRecord } from '../utils/staffTimesheetPersistence';
import { timesheetWeekId, weekIdentity, deriveTimesheetCandidates, mergeTimesheetLines, timesheetTotals, submissionReadiness, TIMESHEET_STATUS } from '../utils/staffTimesheet';

const scopeKeyOf = (uid, org) => (uid && org ? `${uid}::${org}` : '');

export function useStaffTimesheet(authUid, { organizationId = '', memberUserId = '', timeZone = '', weekReference = null, reloadNonce = 0 } = {}) {
  const scoped = !!(authUid && organizationId && memberUserId);
  const requestedScope = scopeKeyOf(authUid, organizationId);
  const week = useMemo(() => weekIdentity(weekReference || new Date(), timeZone || 'UTC'), [weekReference, timeZone]);

  // Scope-keyed snapshot with PER-SOURCE readiness. Each of the three evidence readers is pending|ready|error;
  // the timesheet is usable only when ALL are ready, and unverified if ANY errored.
  const [snap, setSnap] = useState({ scopeKey: '', bookings: [], lessons: [], bookingAuth: 'pending', bookingLegacy: 'pending', lessonsState: 'pending' });
  useEffect(() => {
    setSnap({ scopeKey: requestedScope, bookings: [], lessons: [], bookingAuth: 'pending', bookingLegacy: 'pending', lessonsState: 'pending' });
    if (!scoped) return undefined;
    const bAuth = new Map(); const bLegacy = new Map(); const lMap = new Map();
    const st = { bookingAuth: 'pending', bookingLegacy: 'pending', lessonsState: 'pending' };
    const emit = () => setSnap({ scopeKey: requestedScope, bookings: [...bAuth.values(), ...bLegacy.values()], lessons: [...lMap.values()], ...st });
    const authQ = query(collection(db, 'bookings'), where('instructorAuthUserId', '==', authUid), where('organizationId', '==', organizationId));
    const legacyQ = query(collection(db, 'bookings'), where('instructorId', '==', authUid), where('organizationId', '==', organizationId));
    const lessonsQ = query(collection(db, 'lessonInstances'), where('instructorAuthUserId', '==', authUid), where('organizationId', '==', organizationId));
    const u1 = onSnapshot(authQ, (s) => { bAuth.clear(); s.docs.forEach((d) => bAuth.set(d.id, normalizeBooking({ id: d.id, ...d.data() }))); st.bookingAuth = 'ready'; emit(); }, (e) => { console.error('ts bookings(auth) error', e); st.bookingAuth = 'error'; emit(); });
    const u2 = onSnapshot(legacyQ, (s) => { bLegacy.clear(); s.docs.forEach((d) => { if (!bAuth.has(d.id)) bLegacy.set(d.id, normalizeBooking({ id: d.id, ...d.data() })); }); st.bookingLegacy = 'ready'; emit(); }, (e) => { console.error('ts bookings(legacy) error', e); st.bookingLegacy = 'error'; emit(); });
    const u3 = onSnapshot(lessonsQ, (s) => { lMap.clear(); s.docs.forEach((d) => lMap.set(d.id, normalizeLessonInstanceRecord(d, d.id))); st.lessonsState = 'ready'; emit(); }, (e) => { console.error('ts lessons error', e); st.lessonsState = 'error'; emit(); });
    return () => { u1(); u2(); u3(); };
  }, [authUid, organizationId, requestedScope, scoped, reloadNonce]);

  // Persisted timesheet — Pass 83C.2 §6/§7/§9 — SCOPE-KEYED to the timesheet DOC id (org+memberUserId+week). The
  // record is exposed ONLY when the snapshot's scopeKey matches the CURRENTLY requested id, so a week/member
  // switch never transiently exposes a prior week's frozen/manual state (suppressed AT RENDER, not just cleared
  // in an effect). §11 — a read FAILURE fails closed (error), never "no saved timesheet".
  const requestedTimesheetId = timesheetWeekId(organizationId, memberUserId, week.weekStartKey);
  const [persistSnap, setPersistSnap] = useState({ scopeKey: '', state: 'pending', record: null });
  useEffect(() => {
    setPersistSnap({ scopeKey: requestedTimesheetId, state: 'pending', record: null });
    if (!scoped || !requestedTimesheetId) { setPersistSnap({ scopeKey: requestedTimesheetId, state: scoped ? 'error' : 'pending', record: null }); return undefined; }
    let cancelled = false;
    getDoc(doc(db, 'staffTimesheets', requestedTimesheetId))
      .then((s) => { if (!cancelled) setPersistSnap({ scopeKey: requestedTimesheetId, state: 'ready', record: s.exists() ? normalizeTimesheetRecord(s, requestedTimesheetId) : null }); })
      .catch((e) => { if (!cancelled) { console.error('ts persisted read error', e); setPersistSnap({ scopeKey: requestedTimesheetId, state: 'error', record: null }); } });
    return () => { cancelled = true; };
  }, [requestedTimesheetId, scoped, reloadNonce]);

  // Synchronous scope binding: only trust the persisted record when it was loaded for the current id.
  const persistMatched = persistSnap.scopeKey === requestedTimesheetId;
  const persistState = persistMatched ? persistSnap.state : 'pending';
  const persisted = persistMatched ? persistSnap.record : null;

  const matched = scoped && snap.scopeKey === requestedScope;
  const evidenceError = matched && (snap.bookingAuth === 'error' || snap.bookingLegacy === 'error' || snap.lessonsState === 'error' || persistState === 'error');
  const evidenceReady = matched && snap.bookingAuth === 'ready' && snap.bookingLegacy === 'ready' && snap.lessonsState === 'ready' && persistState === 'ready';

  const derived = useMemo(() => {
    if (!evidenceReady || evidenceError) return { lines: [], totals: { teachingMinutes: 0, otherMinutes: 0, totalAttestedMinutes: 0 } };
    const candidates = deriveTimesheetCandidates({ bookings: snap.bookings, lessons: snap.lessons, week, timeZone: timeZone || week.timezone });
    // A SUBMITTED sheet is frozen — show its persisted lines, not a fresh derivation (§36).
    const lines = (persisted && persisted.status === TIMESHEET_STATUS.SUBMITTED)
      ? persisted.lines
      : mergeTimesheetLines(candidates, persisted ? persisted.lines : []);
    return { lines, totals: timesheetTotals(lines) };
  }, [evidenceReady, evidenceError, snap.bookings, snap.lessons, week, timeZone, persisted]);

  const status = persisted ? persisted.status : TIMESHEET_STATUS.DRAFT;
  const readiness = useMemo(() => submissionReadiness(derived.lines, week), [derived.lines, week]);

  return {
    // usable only when all evidence + persisted read are ready and none errored.
    loading: scoped ? !(evidenceReady || evidenceError) : false,
    unverified: evidenceError,       // §10 — reads failed; the UI must block confirm/save/submit + offer retry
    scopeMissing: !scoped,
    week,
    status,
    persisted,
    lines: derived.lines,
    totals: derived.totals,
    readiness,
    frozen: !!(persisted && persisted.status === TIMESHEET_STATUS.SUBMITTED),
  };
}

export default useStaffTimesheet;
